import Bar from './Bar.js';
import StringHelper from '../../Core/helper/StringHelper.js';
import DomSync from '../../Core/helper/DomSync.js';
import Tooltip from '../../Core/widget/Tooltip.js';
import DomHelper from '../../Core/helper/DomHelper.js';
import ResizeMonitor from '../../Core/helper/ResizeMonitor.js';

/**
 * @module Grid/view/Header
 */

/**
 * The Grid header, which contains simple columns but also allows grouped columns. One instance is created and used per SubGrid
 * automatically, you should not need to instantiate this class manually. See {@link Grid.column.Column} for information about
 * column configuration.
 *
 * {@inlineexample Grid/view/Header.js}
 *
 * @extends Grid/view/Bar
 * @internal
 */
export default class Header extends Bar {
    static $name = 'Header';

    static type = 'gridheader';

    construct(...args) {
        this.handleColumnResize = this.handleColumnResize.bind(this);

        super.construct(...args);
    }

    //region Positioning

    // Reposition all cells when any header is resized, since its size will affect the position of cells following it
    // (could be slightly optimized by only repositioning the affected cells)
    handleColumnResize() {
        if (!this.isDestroyed && this.subGrid) {
            this.cachePositions();
            this.subGrid.grid.rowManager.positionCells();
        }
    }

    // Cache position of all headers, used to position the cells in the grid.
    cachePositions() {
        const
            me                      = this,
            { rtl, contentElement } = me,
            containerRect           = contentElement.getBoundingClientRect(),
            { scrollLeft }          = contentElement;

        for (const column of me.columns.visibleColumns) {
            const headerElement = me.getHeader(column.id);
            if (headerElement) {
                const
                    headerRect = headerElement.getBoundingClientRect(),
                    newWidth   = headerRect.width,
                    oldWidth   = column.meta.width;

                // Calculate position relative to container, accounting for scroll position
                let x = headerRect.left - containerRect.left + scrollLeft;

                if (rtl) {
                    x = containerRect.width - x - newWidth;
                }

                column.meta.width = newWidth;
                column.meta.x = x;

                if (oldWidth !== newWidth) {
                    column.internalOnWidthChanged?.({ width : newWidth, oldWidth });
                }
            }
        }
    }

    observe(element) {
        ResizeMonitor.addResizeListener(element, this.handleColumnResize);
    }

    unobserve(element) {
        ResizeMonitor.removeResizeListener(element, this.handleColumnResize);
    }

    //endregion

    get subGrid() {
        return this._subGrid;
    }

    set subGrid(subGrid) {
        this._subGrid = this.owner = subGrid;
    }

    get region() {
        return this.subGrid?.region;
    }

    changeElement(element, was) {
        const { region } = this;

        // Columns must be examined for maxDepth
        this.getConfig('columns');

        return super.changeElement({
            className : {
                'b-grid-header-scroller'             : 1,
                [`b-grid-header-scroller-${region}`] : region
            },
            role     : null,
            children : [{
                reference : 'headersElement',
                className : {
                    'b-grid-headers'             : 1,
                    [`b-grid-headers-${region}`] : region
                },
                dataset : {
                    region,
                    reference : 'headersElement',
                    maxDepth  : this.maxDepth
                }
            }]
        }, was);
    }

    get overflowElement() {
        return this.headersElement;
    }

    /**
     * Recursive column header config creator.
     * Style not included because of CSP. Widths are fixed up in
     * {@link #function-fixHeaderWidths}
     * @private
     */
    getColumnConfig(column) {
        if (column.isVisible) {
            const
                {
                    id,
                    align,
                    resizable,
                    isLeaf,
                    isParent,
                    isLastInSubGrid,
                    cls,
                    childLevel,
                    field,
                    tooltip,
                    children,
                    isFocusable,
                    grid
                }                = column,
                // Headers tested standalone - may be no grid
                focusedCell      = grid?.focusedCell,
                isFocused        = focusedCell?.rowIndex === -1 && focusedCell?.column === column,
                style            = {};

            let textContentStyle = null;

            if (column.headerWritingMode) {
                textContentStyle = { writingMode : column.headerWritingMode };
            }
            if (column.flex) {
                style.flex = column.flex;
            }
            else if (column.width) {
                style.width = DomHelper.setLength(column.width);
            }
            return {
                style,
                className : {
                    'b-grid-header'                       : 1,
                    'b-grid-header-parent'                : isParent,
                    [`b-level-${childLevel}`]             : 1,
                    [`b-depth-${column.meta.depth ?? 0}`] : 1,
                    [`b-grid-header-align-${align}`]      : align,
                    'b-grid-header-resizable'             : resizable && isLeaf,
                    [cls]                                 : cls,
                    'b-collapsible'                       : column.collapsible,
                    'b-last-parent'                       : isParent && isLastInSubGrid,
                    'b-last-leaf'                         : isLeaf && isLastInSubGrid,
                    'b-header-widgets'                    : column.headerWidgets?.length
                },
                role                            : isFocusable ? 'columnheader' : null,
                'aria-sort'                     : 'none',
                'aria-label'                    : column.ariaLabel,
                [isFocusable ? 'tabIndex' : ''] : isFocused ? 0 : -1,
                dataset                         : {
                    ...Tooltip.encodeConfig(tooltip),
                    columnId                : id,
                    [field ? 'column' : ''] : field
                },
                children : [{
                    className : 'b-grid-header-text',
                    role      : null,
                    children  : [{
                        [grid && isFocusable ? 'id' : ''] : `${grid?.id}-column-${column.id}`,
                        style                             : textContentStyle,
                        className                         : {
                            'b-grid-header-text-content' : 1,
                            'b-has-text'                 : Boolean(column.text),
                            'b-vertical-text'            : column.hasVerticalText
                        }
                    }]
                }, children ? {
                    className   : 'b-grid-header-children',
                    children    : children.map(child => this.getColumnConfig(child)),
                    syncOptions : {
                        syncIdField : 'columnId'
                    }
                } : null,
                {
                    className : 'b-grid-header-resize-handle'
                }]
            };
        }
    }

    // used by safari to fix flex when rows width shrink below this value
    calculateMinWidthForSafari() {
        let minWidth = 0;

        this.columns.visibleColumns.forEach(column => {
            minWidth += column.calculateMinWidth();
        });

        return minWidth;
    }

    /**
     * Fix header widths (flex or fixed width) after rendering. Not a part of template any longer because of CSP
     * @private
     */
    fixHeaderWidths() {
        this.fixCellWidths();
    }

    refreshHeaders() {
        const me = this;

        // run renderers, not done from template to work more like cell rendering
        me.columns.traverse(column => me.refreshColumn(column));

        me.fixHeaderWidths();
        me.cachePositions();
    }

    refreshColumn(column) {
        const
            me            = this,
            headerElement = me.getBarCellElement(column.id);

        if (headerElement) {
            let html = column.headerText;
            if (column.headerRenderer) {
                html = column.headerRenderer.call(column.thisObj || me, { column, headerElement });
                if (DomHelper.isReactElement(html)) {
                    return  DomHelper.handleReactHeaderElement(column, headerElement, html);
                }
            }

            if (column.headerWidgetMap) {
                Object.values(column.headerWidgetMap).forEach(widget => {
                    widget.render(column.textWrapper);
                });
            }

            if (column.icon) {
                html = `<i class="${StringHelper.encodeHtml(column.icon)}"></i>` + (html || '');
            }

            const innerEl = headerElement.querySelector('.b-grid-header-text-content');
            if (innerEl) {
                innerEl.innerHTML = html || '';
            }
        }
    }

    get columns() {
        const
            me     = this,
            result = super.columns;

        if (!me.columnsDetacher) {
            // columns is a chained store, it will be repopulated from master when columns change.
            // That action always triggers change with action dataset.
            me.columnsDetacher = result.ion({
                change() {
                    me.initDepths();
                },
                thisObj : me
            });

            me.initDepths();
        }

        return result;
    }

    set columns(columns) {
        super.columns = columns;
    }

    /**
     * Depths are used for styling of grouped headers. Sets them on meta.
     * @private
     */
    initDepths(columns = this.columns.topColumns, parent = null) {
        const me     = this;
        let maxDepth = 0;

        if (parent?.meta) {
            parent.meta.depth++;
        }

        for (const column of columns) {
            const { meta } = column;
            // <remove-on-release>
            // TODO: this should maybe move
            // </remove-on-release>
            meta.depth = 0;

            if (column.children) {
                me.initDepths(column.children.filter(me.columns.chainedFilterFn), column);
                if (meta.depth && parent) {
                    parent.meta.depth += meta.depth;
                }
            }

            if (meta.depth > maxDepth) {
                maxDepth = meta.depth;
            }
        }

        if (!parent) {
            me.maxDepth = maxDepth;
        }

        return maxDepth;
    }

    //endregion

    //region Getters

    /**
     * Get the header cell element for the specified column.
     * @param {String} columnId Column id
     * @returns {HTMLElement} Header cell element
     */
    getHeader(columnId) {
        return this.getBarCellElement(columnId);
    }

    //endregion

    get contentElement() {
        return this.element.firstElementChild;
    }

    refreshContent() {
        const me = this;

        DomSync.sync({
            domConfig : {
                children     : me.columns.topColumns.map(col => me.getColumnConfig(col)),
                onlyChildren : true
            },
            targetElement    : me.contentElement,
            strict           : true,
            syncIdField      : 'columnId',
            releaseThreshold : 0,
            callback({ action, targetElement, domConfig }) {
                if (domConfig?.className?.['b-grid-header'] && !domConfig.className['b-grid-header-parent']) {
                    if (action === 'newElement') {
                        me.observe(targetElement);
                    }
                    else if (action === 'removeElement') {
                        me.unobserve(targetElement);
                    }
                }
            }
        });

        me.refreshHeaders();
    }

    onInternalPaint({ firstPaint }) {
        if (firstPaint) {
            this.refreshContent();
        }
    }
}

// Register this widget type with its Factory
Header.initClass();
